import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import type { BlogPost } from '@inafana/shared';
import { api, ApiError } from '@/lib/api';
import { usePageMeta } from '../usePageMeta';
import { CtaBanner } from '../components/CtaBanner';
import { RichText, countWords } from './RichText';
import { formatPostDate } from './BlogIndex';

/** 220 wpm — a considered reading pace for technical writing. */
function readingTime(words: number): string {
  return `${Math.max(1, Math.round(words / 220))} min read`;
}

export function BlogPostPage(): React.JSX.Element {
  const { slug } = useParams<{ slug: string }>();

  const { data, isPending, error } = useQuery({
    queryKey: ['blog', 'post', slug],
    queryFn: () => api<{ post: BlogPost }>(`/blog/${slug ?? ''}`),
    enabled: Boolean(slug),
    // A missing slug is an answer, not a failure — don't retry it.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });

  const post = data?.post;
  const notFound = error instanceof ApiError && error.status === 404;

  usePageMeta(
    post ? `${post.title} — Inafana` : 'Notes — Inafana',
    post?.excerpt,
  );

  if (isPending) {
    return (
      <section className="post-page">
        <div className="container narrow">
          <div className="post-head" aria-hidden="true">
            <span className="sk sk-sm" />
            <span className="sk sk-xl" />
            <span className="sk sk-md" />
          </div>
        </div>
      </section>
    );
  }

  if (notFound || !post) {
    return (
      <section className="post-page">
        <div className="container narrow">
          <div className="blog-empty">
            <h1 className="h2">{notFound ? 'That note does not exist' : 'This note could not be loaded'}</h1>
            <p>
              {notFound
                ? 'The link may be out of date, or the post may have been unpublished.'
                : 'Something went wrong on our side. Try refreshing the page.'}
            </p>
            <Link to="/blog" className="btn btn-primary">
              Read the other notes <span className="arrow">→</span>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const words = countWords(post.content);

  return (
    <>
      <article className="post-page">
        <div className="blob blob-blue" style={{ width: 480, height: 480, top: -200, left: '-10%' }} />
        <div className="container narrow">
          <Link to="/blog" className="post-back">
            <ArrowLeft size={15} strokeWidth={2} />
            All notes
          </Link>

          <header className="post-head">
            <div className="post-meta">
              <span>{formatPostDate(post.publishedAt)}</span>
              <span className="post-meta-dot" aria-hidden="true">
                ·
              </span>
              <span>{readingTime(words)}</span>
            </div>
            <h1 className="h1">{post.title}</h1>
            <p className="lede">{post.excerpt}</p>
          </header>

          <RichText content={post.content} />

          <div className="post-foot">
            <Link to="/blog" className="btn btn-ghost">
              <ArrowLeft size={15} strokeWidth={2} /> All notes
            </Link>
          </div>
        </div>
      </article>

      <CtaBanner
        title={
          <>
            Building something <span className="si">like this</span>?
          </>
        }
        lede="We build agentic AI systems and the software they run on. Tell us what you are working on and we will tell you honestly whether we are the right team for it."
        primaryLabel="Start a project"
      />
    </>
  );
}
